import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken, fileUrl } from "../lib/api";
import { applyTheme, cacheBranding, getCachedBranding, clearBranding } from "../lib/theme";

const AuthCtx = createContext(null);

function applyBranding(company) {
  if (!company) return;
  applyTheme(company.theme);
  cacheBranding(company);
  document.title = company.app_name || company.name || "KONVIA";
  const link = document.getElementById("favicon-link");
  if (link) link.href = company.favicon_file_id ? fileUrl(company.favicon_file_id) : company.logo_file_id ? fileUrl(company.logo_file_id) : "/favicon.ico";
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const setUser = useCallback((u) => { setUserState(u); if (u && u.company) applyBranding(u.company); }, []);

  useEffect(() => { const b = getCachedBranding(); if (b && getToken()) applyBranding(b); else if (!getToken()) clearBranding(); }, []);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const h = () => { const b = getCachedBranding(); if (b) applyTheme(b.theme); };
    mq?.addEventListener?.("change", h);
    return () => mq?.removeEventListener?.("change", h);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  useEffect(() => { if (user && user.company) applyBranding(user.company); }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };
  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    clearBranding();
    setUser(false);
  };

  const switchCompany = async (companyId) => {
    const { data } = await api.post("/auth/switch", { company_id: companyId });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const can = (perm) => !!(user && user.effective && user.effective[perm] !== undefined);
  const scope = (perm) => user?.effective?.[perm];

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, refresh, login, register, logout, switchCompany, can, scope }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
